import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Box,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, DoneAll as DoneAllIcon } from '@mui/icons-material';

import { useLegislationNotifications } from '../hooks/useLegislationNotifications';
import { formatOfficialDateBR } from '../utils/officialDate';
import { buildNotificationFocusPath } from '../utils/changeTableFocus';
import { LegislationNotificationIcon } from './LegislationNotificationIcon';

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

export function NotificationPanel({ anchorEl, open, onClose }: NotificationPanelProps) {
  const navigate = useNavigate();
  const { notifications, unreadCount, isLoading, markRead, markAllRead } =
    useLegislationNotifications(open);

  const handleMarkAll = async () => {
    try {
      await markAllRead();
    } catch {
      // silencioso: se falhar, os alertas seguem como estavam
    }
  };

  const handleOpenNotification = async (notification: {
    id: number;
    change_id: number;
    is_monitored: boolean;
  }) => {
    try {
      await markRead(notification.id);
    } catch {
      // Navegação e foco na tabela não dependem da marcação como lida.
    }
    onClose();
    navigate(buildNotificationFocusPath(notification.change_id), {
      state: { focusChangeId: notification.change_id },
    });
  };

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: anchorEl ? anchorEl.getBoundingClientRect().bottom + 8 : 0,
        right: anchorEl ? window.innerWidth - anchorEl.getBoundingClientRect().right : 0,
        width: 380,
        maxHeight: 500,
        overflow: 'hidden',
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        zIndex: 1300,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Alertas
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {unreadCount} não {unreadCount === 1 ? 'lida' : 'lidas'} · novas normas (30 dias)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            onClick={handleMarkAll}
            disabled={unreadCount === 0}
            startIcon={<DoneAllIcon />}
          >
            Limpar
          </Button>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon />}>
            Fechar
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma notificação nos últimos 30 dias
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem disablePadding sx={{ opacity: notification.is_read ? 0.72 : 1 }}>
                  <ListItemButton
                    onClick={() => handleOpenNotification(notification)}
                    sx={{ alignItems: 'flex-start', gap: 1.5, py: 1.5, px: 2 }}
                  >
                    <Box sx={{ pt: 0.25, display: 'flex', alignItems: 'center' }}>
                      <LegislationNotificationIcon isMonitored={notification.is_monitored} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={notification.is_read ? 400 : 600}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatOfficialDateBR(notification.created_at)}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
}
